// AdvancedLineupPanel.jsx
import React, { useMemo, useEffect, useState } from "react";

const AdvancedLineupPanel = ({
  allTags,
  allGames,
  allSports,
  handleLineupGenerate,
  selectedSports,
  setSelectedSports,
  onSportsChange,

  // NEW: parent-controlled pick count (lineup size)
  advLineupSize,
  setAdvLineupSize,
}) => {
  // mix + filters
  const [mixType, setMixType] = useState("EVEN"); // default named mix
  const [maxLineups, setMaxLineups] = useState(20);
  const [lineupTagFilter, setLineupTagFilter] = useState([]);
  const [lineupGameFilter, setLineupGameFilter] = useState([]);

  const isSelected = (s) =>
    Array.isArray(selectedSports) && selectedSports.includes(s);

  const toggleSport = (sport, checked) => {
    const next = checked
      ? Array.from(new Set([...(selectedSports || []), sport]))
      : (selectedSports || []).filter((s) => s !== sport);

    setSelectedSports?.(next);
    onSportsChange?.(next);
    setLineupGameFilter([]); // clear because universe of games changed
  };

  // ---------- A) Pick Count control ----------
  const pickSizes = [2, 3, 4, 5, 6];

  // ---------- B) Mix options depend on pick count ----------
  const NAMED_MIX = [
    { value: "EVEN",         label: "Even Mix" },
    { value: "OVERS_HEAVY",  label: "Overs Heavy" },
    { value: "UNDERS_HEAVY", label: "Unders Heavy" },
    { value: "FLEX",         label: "Flex (no strict mix)" },
  ];

  const fixedMixesFor = (size) => {
    // Produce size+1 fixed recipes: size/0 ... 0/size
    const out = [];
    for (let over = size; over >= 0; over--) {
      const under = size - over;
      out.push({
        value: `${over}_OVER_${under}_UNDER`,
        label: `${over} Over / ${under} Under`,
      });
    }
    return out;
  };

  const mixOptions = useMemo(
    () => [...NAMED_MIX, ...fixedMixesFor(advLineupSize)],
    [advLineupSize]
  );

  useEffect(() => {
    setLineupGameFilter((prev) => prev.filter((g) => allGames.includes(g)));
  }, [allGames]);
  
  // If user changes pick count and current fixed recipe no longer matches,
  // reset to a safe default.
  useEffect(() => {
    const m = String(mixType).toUpperCase().match(/(\d+)_?OVER_?(\d+)_?UNDER/);
    if (m && Number(m[1]) + Number(m[2]) !== Number(advLineupSize)) {
      setMixType("EVEN");
    }
  }, [advLineupSize, mixType]);

  const onGenerate = () => {
    handleLineupGenerate({
      mixType,
      maxLineups,
      tags: lineupTagFilter,
      games: lineupGameFilter,
      sports: selectedSports || [],
      lineupSize: Number(advLineupSize || 6),   // pass the pick count
    });
  };

  const baseStyle   = { borderRadius: "10px", padding: "0.6rem 0.75rem", fontSize: "1rem", fontWeight: 500, border: "1px solid #ccc", backgroundColor: "#fefefe", color: "#333", minWidth: "160px" };
  const labelStyle  = { fontWeight: 600, fontSize: ".85rem", marginBottom: ".25rem", display: "inline-block" };
  const sectionWrap = { display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem", alignItems: "flex-start" };

  return (
    <div className="lineup-advanced-panel" style={{ padding: "1.5rem", background: "#f9f9f9", borderRadius: "8px", marginTop: "2rem" }}>
      <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
        {/* Include Sports */}
        <div>
          <label style={labelStyle}>Include Sports</label><br />
          <div style={{ display: "flex", flexDirection: "column", gap: ".5rem", padding: ".75rem", border: "1px solid #ccc", borderRadius: "12px", background: "#fefefe", maxHeight: "200px", overflowY: "auto", minWidth: "200px", fontSize: ".95rem" }}>
            {allSports.map((sport) => (
              <label key={sport} style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                <input
                  type="checkbox"
                  value={sport}
                  checked={isSelected(sport)}
                  onChange={(e) => toggleSport(sport, e.target.checked)}
                />
                {sport}
              </label>
            ))}
          </div>
        </div>

        {/* Filter by Game */}
        <div>
          <label style={labelStyle}>Filter by Game</label><br />
          <select
            multiple
            value={lineupGameFilter}
            onChange={(e) => setLineupGameFilter([...e.target.selectedOptions].map((o) => o.value))}
            style={{ ...baseStyle, minHeight: "200px", minWidth: "280px", fontSize: ".95rem", padding: ".75rem" }}
          >
            {allGames.map((game) => (
              <option key={game} value={game}>{game}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={sectionWrap}>
        {/* NEW: Pick Count */}
        <div>
          <label style={labelStyle}>Pick Count</label><br />
          <select
            style={baseStyle}
            value={advLineupSize}
            onChange={(e) => setAdvLineupSize(Number(e.target.value))}
          >
            {pickSizes.map((n) => (
              <option key={n} value={n}>{n} Picks</option>
            ))}
          </select>
        </div>

        {/* Dynamic Mix */}
        <div>
          <label style={labelStyle}>Mix Type</label><br />
          <select
            style={baseStyle}
            value={mixType}
            onChange={(e) => setMixType(e.target.value)}
          >
            {mixOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Max Lineups</label><br />
          <input
            type="number"
            value={maxLineups}
            onChange={(e) => setMaxLineups(parseInt(e.target.value || 0, 10))}
            min={1}
            max={500}
            style={baseStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Filter by Tag</label><br />
          <select
            multiple
            value={lineupTagFilter}
            onChange={(e) => setLineupTagFilter([...e.target.selectedOptions].map((o) => o.value))}
            style={{ ...baseStyle, minHeight: "100px" }}
          >
            {allTags.map((tag) => <option key={tag} value={tag}>{tag}</option>)}
          </select>
        </div>
      </div>

      <div className="lineup-advanced-action" style={{ display: "flex", justifyContent: "flex-start" }}>
        <button
          className="lineup-advanced-generate"
          onClick={onGenerate}
          disabled={!selectedSports || selectedSports.length === 0}
          style={{
            padding: ".75rem 1.5rem",
            background: (!selectedSports || selectedSports.length === 0) ? "#999" : "black",
            color: "white",
            border: "none",
            borderRadius: "8px",
            marginBottom: "1rem",
            cursor: (!selectedSports || selectedSports.length === 0) ? "not-allowed" : "pointer",
          }}
        >
          Generate Lineups
        </button>
      </div>
    </div>
  );
};

export default AdvancedLineupPanel;
